/**
 * Cellxpert, as exposed today under `/postbacks/sales-expert`.
 *
 * This is the live production route for Cellxpert brands. The controller carries
 * an explicit migration note: "Currently used by Cellxpert in production.
 * Migration plan: switch Cellxpert to /postbacks/cellxpert, keep this for future
 * Sales Expert integrations." (postbacks.controller.ts:181-182). Until that
 * migration happens, this contract — not the native `/postbacks/cellxpert`
 * contract in `cellxpert-native.ts` — is what real Cellxpert brand postbacks use.
 *
 * `/postbacks/sales-expert` accepts the generic `PostbackQueryDto`, which
 * declares `uid` directly, so `uid=[afp]` here is read correctly.
 */
export const cellxpertContract = {
    id: 'cellxpert',
    displayName: 'Cellxpert',
    route: 'sales-expert',
    trackingTagMacro: '[afp]',
    amountBehavior: 'amount is only sent for QUALIFIED_CPA, from [commissionamount]. currency is not a Cellxpert macro; ' +
        'we hardcode currency=EUR on the CPA link.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    requiresFixedCurrencyForCpa: true,
    pitfalls: [
        'Route is /postbacks/sales-expert, not /postbacks/cellxpert, until the documented migration ships.',
        'currency is not documented by Cellxpert; the CPA link hardcodes currency=EUR.',
    ],
    sourceRefs: [
        'apps/api/src/modules/postbacks/controllers/postbacks.controller.ts:180-218',
        'apps/api/src/modules/postbacks/dto/postback-query.dto.ts (uid declared directly)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'subid', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'uid', macro: '[afp]' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'subid', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'uid', macro: '[afp]' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'subid', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'uid', macro: '[afp]' },
                { ourParam: 'amount', macro: '[commissionamount]' },
                { ourParam: 'currency', macro: 'EUR' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
            fixedParams: { currency: 'EUR' },
        },
    ],
};
/**
 * The native `/postbacks/cellxpert` endpoint, backed by `CellxpertPostbackDto` +
 * `ParameterMapperService.mapCellxpert`. Not yet reachable from any of the four
 * existing UI/bot surfaces as a first-class option (TMA has a "Cellxpert" entry
 * that already speaks this dialect; backoffice and the Telegram bot only expose
 * the sales-expert route above under the `cellxpert` id).
 *
 * `CellxpertPostbackDto` does not declare a `uid` field. Sending `uid=[afp]`
 * here is silently dropped by `ValidationPipe({ whitelist: true })` — this is
 * exactly the bug tracked as DEV-001 ("Cellxpert postbacks send a populated
 * uid=[afp], but normalized events persist external_uid = NULL"). The correct
 * identifier param for this endpoint today is `afp`, which
 * `mapCellxpert` reads into `uid` server-side.
 */
export const cellxpertNativeContract = {
    id: 'cellxpert-native',
    displayName: 'Cellxpert (native /postbacks/cellxpert)',
    route: 'cellxpert',
    trackingTagMacro: '[afp]',
    amountBehavior: 'amount comes from commissionamount, read directly by mapCellxpert into amount.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    requiresFixedCurrencyForCpa: true,
    unsupportedParams: ['uid'],
    pitfalls: [
        'DEV-001 (open as of this writing): CellxpertPostbackDto has no `uid` field. `uid=[afp]` is ' +
            'silently stripped by whitelist:true; use `afp=[afp]` instead, which mapCellxpert reads into uid.',
        'subid is derived server-side from subid (direct) || trackingcode — sending trackingcode=[trackingcode] works.',
        'tracking_tag is derived from tracking_tag (direct) || afp — afp alone is sufficient.',
    ],
    sourceRefs: [
        'apps/api/src/modules/postbacks/controllers/postbacks.controller.ts:495-543',
        'apps/api/src/modules/postbacks/dto/cellxpert-postback.dto.ts',
        'apps/api/src/modules/postbacks/services/parameter-mapper.service.ts:127-158 (mapCellxpert)',
        'apps/api/src/main.ts:48-49 (whitelist: true, forbidNonWhitelisted: false)',
        'backlog/tasks/DEV-001-cellxpert-preserve-uid.md',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'trackingcode', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'afp', macro: '[afp]' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'trackingcode', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'afp', macro: '[afp]' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'trackingcode', macro: '[trackingcode]' },
                { ourParam: 'country', macro: '[isocountry]' },
                { ourParam: 'afp', macro: '[afp]' },
                { ourParam: 'commissionamount', macro: '[commissionamount]' },
                { ourParam: 'currency', macro: 'EUR' },
                { ourParam: 'tracking_tag', macro: '[afp]' },
            ],
            fixedParams: { currency: 'EUR' },
        },
    ],
};
//# sourceMappingURL=cellxpert.js.map